/**
 * Brand Consultant Main Component
 * 
 * Main container component for the Brand Consultant feature.
 * Integrates with existing brand context, credit system, and session management.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import type { ConsultantSession } from './types';
import { useBrands } from '@/lib/BrandsProvider';
import { useCredits } from '@/hooks/useCredits';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MessageCircle, Plus, Clock, Zap, AlertCircle, ChevronDown } from 'lucide-react';
import { ChatInterface } from './ChatInterface';
import { useBrandConsultant } from './hooks/useBrandConsultant';
import { useConsultantSession } from './hooks/useConsultantSession';
import { cn } from '@/lib/utils';

export function BrandConsultant() {
  const { id: brandId } = useParams<{ id: string }>();
  const { selectedBrand, brands, loading: brandsLoading } = useBrands();
  const { } = useCredits();
  const [sessionLoading, setSessionLoading] = useState(false);
  const [availableSessions, setAvailableSessions] = useState<ConsultantSession[]>([]);

  // Get brand from URL or use selected brand
  const currentBrand = brandId 
    ? brands.find(b => b.id === brandId) || selectedBrand
    : selectedBrand;

  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearError,
    resetConversation,
    loadMessagesFromSession,
    initializeWithWelcomeMessage,
    hasAvailableCredits,
    messageCost,
    creditBalance
  } = useBrandConsultant(currentBrand?.id);

  const {
    session,
    loading: sessionHookLoading,
    loadSession,
    getAllSessions,
    switchToSession,
    clearSession
  } = useConsultantSession();

  // Load all available sessions when brand changes
  useEffect(() => {
    if (currentBrand?.id) {
      getAllSessions(currentBrand.id).then(sessions => {
        setAvailableSessions(sessions);
      });
    }
  }, [currentBrand?.id, getAllSessions]);

  // Initialize session when brand is available
  useEffect(() => {
    if (currentBrand?.id && !session && !sessionHookLoading) {
      setSessionLoading(true);
      loadSession(currentBrand.id)
        .then((loadedSession) => {
          if (loadedSession?.id) {
            // Load messages from the session
            loadMessagesFromSession(loadedSession.id);
          } else {
            // If no session loaded, show welcome message
            initializeWithWelcomeMessage();
          }
        })
        .catch((error) => {
          console.error('Error loading session:', error);
          // If session loading fails, show welcome message
          initializeWithWelcomeMessage();
        })
        .finally(() => {
          setSessionLoading(false);
        });
    }
  }, [currentBrand?.id, session, sessionHookLoading, loadSession, loadMessagesFromSession, initializeWithWelcomeMessage]);

  const handleSendMessage = async (content: string): Promise<boolean> => {
    if (!currentBrand?.id) {
      toast({
        title: 'Brand Required',
        description: 'Please select a brand to use the Brand Consultant.',
        variant: 'destructive'
      });
      return false;
    }

    clearError();
    return await sendMessage(content, session?.id);
  };

  const handleNewSession = () => {
    resetConversation();
    if (currentBrand?.id) {
      clearSession(currentBrand.id);
      // Create a new session and initialize with welcome message
      loadSession(currentBrand.id)
        .then((newSession) => {
          if (newSession?.id) {
            // Since it's a new session, show welcome message
            initializeWithWelcomeMessage();
            // Refresh the sessions list
            getAllSessions(currentBrand.id).then(sessions => {
              setAvailableSessions(sessions);
            });
          }
        });
    }
    toast({
      title: '✨ New Session Started',
      description: 'Started a new conversation with your Brand Consultant.',
    });
  };

  const handleSwitchSession = async (sessionId: string) => {
    if (!currentBrand?.id) return;

    setSessionLoading(true);
    try {
      const selectedSession = await switchToSession(sessionId, currentBrand.id);
      if (selectedSession?.id) {
        // Load messages from the selected session
        await loadMessagesFromSession(selectedSession.id);
        toast({
          title: '🔄 Session Switched',
          description: `Switched to session from ${formatSessionDate(selectedSession.created_at)}`,
        });
      }
    } catch (error) {
      console.error('Error switching session:', error);
      toast({
        title: 'Error',
        description: 'Failed to switch session. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSessionLoading(false);
    }
  };

  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Loading state
  if (brandsLoading || sessionLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative flex items-center justify-center h-full">
          <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg p-8">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20" />
            <div className="relative text-center space-y-6">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-2xl inline-block">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
              <div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent mb-2">
                  Loading Brand Consultant
                </h3>
                <p className="text-gray-600">Preparing your personalized AI assistant...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No brand selected
  if (!currentBrand) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative flex items-center justify-center h-full p-6">
          <div className="group relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 max-w-lg w-full">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full translate-y-12 -translate-x-12" />
            <div className="relative p-8 text-center space-y-6">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-2xl inline-block group-hover:from-blue-200 group-hover:to-indigo-300 transition-all duration-300">
                <MessageCircle className="h-12 w-12 text-blue-600 group-hover:text-blue-700 group-hover:scale-110 transition-all duration-300" />
              </div>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent mb-3 group-hover:from-blue-600 group-hover:to-indigo-600 transition-all duration-300">
                  Brand Required
                </h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Please select a brand to start using the Brand Consultant.
                </p>
                <p className="text-sm text-gray-500 leading-relaxed">
                  The Brand Consultant provides personalized insights and strategic guidance based on your specific brand context.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="relative flex flex-col h-full">
      {/* Enhanced Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm m-6 p-6">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
        <div className="relative">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                <MessageCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-1">Brand Consultant</h1>
                <p className="text-gray-600 leading-relaxed">
                  Strategic insights for {currentBrand.brand_name}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Enhanced Credit information */}
              <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm p-3">
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-50/30 via-transparent to-amber-50/20" />
                <div className="relative flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-yellow-100 to-amber-200 rounded-lg shadow-sm">
                    <Zap className="h-4 w-4 text-yellow-600" />
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="text-sm text-gray-600 hidden sm:inline">
                      {messageCost} credits per message
                    </span>
                    <span className="text-sm text-gray-600 sm:hidden">
                      {messageCost} credits/msg
                    </span>
                    <div className={cn(
                      "px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap shadow-sm",
                      hasAvailableCredits 
                        ? "bg-gradient-to-r from-emerald-100 to-green-200 text-emerald-700 border border-emerald-200/50"
                        : "bg-gradient-to-r from-red-100 to-pink-200 text-red-700 border border-red-200/50"
                    )}>
                      Balance: {creditBalance}
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Session management dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-50/20 via-transparent to-purple-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isLoading}
                      className="relative gap-2 w-fit bg-transparent hover:bg-transparent border-0 text-inherit shadow-none p-3"
                    >
                      <div className="p-1.5 bg-gradient-to-br from-indigo-100 to-purple-200 rounded-lg group-hover:from-indigo-200 group-hover:to-purple-300 transition-all duration-300">
                        <Clock className="h-4 w-4 text-indigo-600 group-hover:text-indigo-700" />
                      </div>
                      <span className="hidden sm:inline text-gray-700 group-hover:text-gray-800 font-medium transition-colors duration-300">
                        {session ? formatSessionDate(session.created_at) : 'Session'}
                      </span>
                      <span className="sm:hidden text-gray-700 group-hover:text-gray-800 font-medium transition-colors duration-300">Session</span>
                      <ChevronDown className="h-3 w-3 text-gray-500 group-hover:text-gray-600 transition-colors duration-300" />
                    </Button>
                  </div>
                </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-white/95 backdrop-blur-sm border border-white/50 shadow-xl">
              <DropdownMenuItem onClick={handleNewSession} className="gap-2 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200">
                <Plus className="h-4 w-4" />
                New Session
              </DropdownMenuItem>
              {availableSessions.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  {availableSessions
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .slice(0, 10) // Show only the 10 most recent sessions
                    .map((sessionItem) => (
                      <DropdownMenuItem
                        key={sessionItem.id}
                        onClick={() => handleSwitchSession(sessionItem.id)}
                        className={cn(
                          "gap-2 text-sm hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-200",
                          session?.id === sessionItem.id && "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800"
                        )}
                      >
                        <Clock className="h-3 w-3" />
                        <div className="flex flex-col gap-1">
                          <div className="font-medium">
                            {formatSessionDate(sessionItem.created_at)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {sessionItem.status === 'active' ? 'Active' : 'Archived'}
                          </div>
                        </div>
                        {session?.id === sessionItem.id && (
                          <div className="ml-auto w-2 h-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full shadow-sm" />
                        )}
                      </DropdownMenuItem>
                    ))
                  }
                </>
              )}
              {availableSessions.length === 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-sm text-muted-foreground text-center">
                    No previous sessions
                  </div>
                </>
              )}
            </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Error display */}
      {error && (
        <div className="mx-6 mb-4">
          <div className="group relative overflow-hidden bg-gradient-to-br from-red-50/80 via-pink-50/60 to-white rounded-2xl border border-red-200/60 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/30 via-transparent to-pink-50/20" />
            <div className="relative flex items-center gap-4 p-4">
              <div className="p-2 bg-gradient-to-br from-red-100 to-pink-200 rounded-xl shadow-sm">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold bg-gradient-to-r from-red-800 to-pink-700 bg-clip-text text-transparent">Error</p>
                <p className="text-sm text-red-700/90 leading-relaxed">{error}</p>
              </div>
              <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-lg border border-white/50 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-r from-red-50/20 via-transparent to-pink-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearError}
                  className="relative bg-transparent hover:bg-transparent border-0 text-red-600 hover:text-red-700 shadow-none p-2"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat interface */}
      <ChatInterface
        messages={messages}
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
        hasAvailableCredits={hasAvailableCredits}
        messageCost={messageCost}
        creditBalance={creditBalance}
        brand={currentBrand}
        className="flex-1"
      />
      </div>
    </div>
  );
}